import { useState, useEffect } from 'react';
import { User } from '../../../shared/types';

declare global {
  interface Window {
    google: any;
  }
}

// Modul-weiter Status: wurde google.accounts.id.initialize() bereits aufgerufen?
// Wird von der LoginPage benötigt, um den offiziellen Google-Button erst nach
// erfolgter Initialisierung zu rendern (renderButton setzt initialize voraus).
let gsiInitialized = false;

export const isGsiInitialized = (): boolean => {
  return gsiInitialized;
};

// Abstand, in dem das App-Token erneuert wird, solange jemand angemeldet ist.
// Das Token ist 7 Tage gültig; jede Erneuerung startet diese Frist neu. Erst
// nach 7 Tagen ohne Nutzung ist wieder der Google-Login nötig.
const TOKEN_ERNEUERUNG_INTERVALL_MS = 60 * 60 * 1000;

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadGoogleAPI = () => {
      if (window.google) {
        initializeGoogleSSO();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = () => {
        setTimeout(initializeGoogleSSO, 500);
      };
      script.onerror = () => {
        setLoading(false);
      };
      document.head.appendChild(script);
    };

    const initializeGoogleSSO = () => {
      const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
      
      if (window.google && clientId) {
        try {
          window.google.accounts.id.initialize({
            client_id: clientId,
            callback: handleCredentialResponse,
          });
          gsiInitialized = true;
        } catch (error) {
          console.error('Error initializing Google SSO:', error);
        }
        setLoading(false);
      } else {
        setLoading(false);
      }
    };

    loadGoogleAPI();
  }, []);

  const handleCredentialResponse = async (response: any) => {
    try {
      setLoading(true);
      
      const authResponse = await fetch('/.netlify/functions/auth-callback', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: response.credential
        })
      });

      const result = await authResponse.json();

      if (result.success) {
        setUser(result.user);
        localStorage.setItem('authToken', result.token);
      } else {
        alert('Login fehlgeschlagen: ' + result.error);
      }
    } catch (error) {
      console.error('Login error:', error);
      alert('Login fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    if (window.google) {
      try {
        window.google.accounts.id.prompt();
      } catch (error) {
        console.error('Error calling Google prompt:', error);
        alert('Fehler beim Öffnen des Google Login');
      }
    } else {
      alert('Google API nicht geladen. Bitte Seite neu laden.');
    }
  };

  const handleSignOut = () => {
    setUser(null);
    localStorage.removeItem('authToken');
    if (window.google) {
      window.google.accounts.id.disableAutoSelect();
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (token && !user) {
      try {
        // Base64 URL zu Standard Base64 konvertieren
        const base64UrlDecode = (str: string) => {
          // Ersetze URL-sichere Zeichen
          let base64 = str.replace(/-/g, '+').replace(/_/g, '/');
          
          // Füge Padding hinzu falls nötig
          switch (base64.length % 4) {
            case 2: base64 += '=='; break;
            case 3: base64 += '='; break;
          }
          
          // UTF-8 korrekt decodieren (atob allein kann keine Multi-Byte UTF-8 Zeichen wie Umlaute)
          const bytes = Uint8Array.from(atob(base64), c => c.charCodeAt(0));
          return new TextDecoder('utf-8').decode(bytes);
        };

        const payloadBase64 = token.split('.')[1];
        const payloadJson = base64UrlDecode(payloadBase64);
        const payload = JSON.parse(payloadJson);
        
        if (payload.exp > Date.now() / 1000) {
          setUser({
            name: payload.name,
            email: payload.email,
            picture: payload.picture
          });
        } else {
          console.log('Token expired, removing from localStorage');
          localStorage.removeItem('authToken');
        }
      } catch (error) {
        console.error('Token parsing error:', error);
        localStorage.removeItem('authToken');
      }
    }
    setLoading(false);
  }, [user]);

  // Erneuerung des App-Tokens beim Start und danach stündlich, solange jemand
  // angemeldet ist. Weist der Server die Erneuerung ab (Token abgelaufen oder
  // Mitarbeiter nicht mehr aktiv), geht es zurück zur Anmeldung. Bei einer
  // Netzwerkstörung bleibt das bisherige Token bestehen.
  useEffect(() => {
    if (!user) {
      return;
    }

    const erneuereToken = async () => {
      const token = localStorage.getItem('authToken');
      if (!token) {
        return;
      }

      try {
        const response = await fetch('/.netlify/functions/auth-refresh', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.status === 401 || response.status === 403) {
          console.warn('App-Token konnte nicht erneuert werden, Rückkehr zur Anmeldung');
          localStorage.removeItem('authToken');
          window.location.reload();
          return;
        }

        if (!response.ok) {
          console.error('Erneuerung des App-Tokens fehlgeschlagen, Status:', response.status);
          return;
        }

        const result = await response.json();
        if (result.success && result.token) {
          localStorage.setItem('authToken', result.token);
        }
      } catch (error) {
        console.error('Erneuerung des App-Tokens fehlgeschlagen:', error);
      }
    };

    erneuereToken();
    const intervall = setInterval(erneuereToken, TOKEN_ERNEUERUNG_INTERVALL_MS);
    return () => clearInterval(intervall);
  }, [user]);

  return {
    user,
    loading,
    handleGoogleLogin,
    handleSignOut
  };
};
